import { createSelector } from 'reselect';
import { currentRouteSelector } from '../../CommonComponents/Router/Selector';
import { currentClientSelector } from '../../CommonSelectors/CommonAreaSelector';

const netWorthSidebarSelector = state => state.NetWorthSidebar;

export const errorsSelector = createSelector(netWorthSidebarSelector, ({ errors }) => errors);

export const isFetchingSelector = createSelector(
  netWorthSidebarSelector,
  ({ isFetching }) => isFetching,
);

export const accountGroupsSelector = createSelector(
  netWorthSidebarSelector,
  ({ accountGroups }) => accountGroups,
);

export const selectAreManagedAccountGroupsSelectable = createSelector(
  accountGroupsSelector,
  accountGroups => {
    const investmentAccountsGroup = accountGroups.find(group => group.Name === 'Investment');

    if (!investmentAccountsGroup) {
      return false;
    }

    return investmentAccountsGroup.Items.some(item => item.Groups.some(group => group.IsManaged));
  },
);

export const selectManagedAccountGroups = createSelector(
  accountGroupsSelector,
  selectAreManagedAccountGroupsSelectable,
  (accountGroups, areManagedAccountGroupsSelectable) => {
    const investmentAccountsGroup = accountGroups.find(group => group.Name === 'Investment');

    if (!investmentAccountsGroup) {
      return {};
    }

    const { Items } = investmentAccountsGroup;

    return Items.reduce((acc, account) => {
      if (account.Item && account.Item.IsOrionManagedAccount) {
        const groupIdentifier = account.GroupId
          ? account.GroupId
          : account.GroupName
          ? account.GroupName.join()
          : '';

        const name = account.GroupName && account.GroupName[0] ? account.GroupName[0] : '';
        const nameId = account.GroupName && account.GroupName[1] ? account.GroupName[1] : '';

        return {
          ...acc,
          [groupIdentifier]: {
            portfolioGroup: {
              id: account.GroupId,
              name,
              nameId,
              selectable: areManagedAccountGroupsSelectable && account.GroupId,
            },
            accounts: [...(acc[groupIdentifier] ? acc[groupIdentifier].accounts : []), account],
          },
        };
      }

      return acc;
    }, {});
  },
);

export const selectUnManagedAccountGroups = createSelector(
  accountGroupsSelector,
  selectAreManagedAccountGroupsSelectable,
  accountGroups => {
    const investmentAccountsGroup = accountGroups.find(group => group.Name === 'Investment');

    if (!investmentAccountsGroup) {
      return {};
    }

    const { Items } = investmentAccountsGroup;

    return Items.reduce((acc, account) => {
      if (account.Item && !account.Item.IsOrionManagedAccount) {
        const groupIdentifier = account.GroupName ? account.GroupName.join() : '';

        return {
          ...acc,
          [groupIdentifier]: {
            accounts: [...(acc[groupIdentifier] ? acc[groupIdentifier].accounts : []), account],
          },
        };
      }

      return acc;
    }, {});
  },
);

export const accountsSelector = createSelector(netWorthSidebarSelector, ({ accounts }) => accounts);

export const selectAccountsGroupedBy = createSelector(accountGroupsSelector, accountGroups => {
  const investmentAccountsGroup = accountGroups.find(group => group.Name === 'Investment');

  return (investmentAccountsGroup && investmentAccountsGroup.GroupedBy) || null;
});

export const totalNetWorthSelector = createSelector(
  netWorthSidebarSelector,
  ({ totalNetWorth }) => totalNetWorth,
);

export const dataVersionSelector = createSelector(
  netWorthSidebarSelector,
  ({ dataVersion }) => dataVersion,
);

export const isNetworthActiveSelector = createSelector(
  currentRouteSelector,
  ({ controller }) => controller === 'NetWorth',
);

export const hasOrionManagedAccountSelector = createSelector(
  currentClientSelector,
  accountsSelector,
  (client, accounts) =>
    client.IsOrionClient && Object.values(accounts).some(account => account.IsOrionManagedAccount),
);

export const hasLinkedAccountsSelector = createSelector(accountsSelector, accounts =>
  Object.values(accounts).some(({ IsLinked }) => IsLinked),
);

export const allowAddManualAccountsSelector = createSelector(
  netWorthSidebarSelector,
  ({ allowAddManualAccount }) => allowAddManualAccount,
);

export const selectUnmanagedAccountsLabel = createSelector(
  netWorthSidebarSelector,
  ({ unmanagedAccountsLabel }) => unmanagedAccountsLabel ?? 'Unmanaged',
);
